import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, authApi } from "../Apis/Api";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import "react-phone-number-input/style.css";
import { validateImageFile } from "../utils/validateImageFile";
import { handleApiResponse } from "../utils/response";
import CustomPhoneInput from "../components/common/CustomPhoneInput";
import { auth } from "../firebase";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { generateSecurePassword } from "../utils/generatePassword";
import { usePermissions } from "../context/PermissionContext";
import { toast } from "react-toastify";
import { saveFcmToken } from "../utils/saveFcmToken";
import logo from "../assets/Group 2085662814.png"; // ← Yeh line add karo
import { parsePhoneNumberFromString } from "libphonenumber-js";
// API call to check account existence
const checkAccountExistence = async (data) => {
  try {
    const response = await fetch(
      "https://pevbackend.codovio.com/public/api/check-account-existence",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return (
      result.exists ||
      result.status === "exists" ||
      result.message?.includes("already exists") ||
      result.data?.exists === true ||
      result.error?.includes("already exists")
    );
  } catch (error) {
    console.error("Error checking account existence:", error);
    return false;
  }
};

// Validation Schema
const validationSchema = Yup.object().shape({
  company_name: Yup.string()
    .required("Company Name is required")
    .min(2, "Company Name must be at least 2 characters")
    .max(50, "Company Name must not exceed 50 characters"),
  client_name: Yup.string()
    .required("Client Name is required")
    .min(2, "Client Name must be at least 2 characters")
    .max(20, "Client Name must not exceed 20 characters"),
  email: Yup.string()
    .required("Email is required")
    .email("Please enter a valid email address"),
  phone_no: Yup.string()
    .nullable()
    .test("valid-phone", "Please enter a valid phone number", (value) => {
      if (!value) return true;
      return /^\+[1-9]\d{1,14}$/.test(value);
    }),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
    ),
  logo: Yup.string(),
  agreeToTerms: Yup.boolean().oneOf(
    [true],
    "You must agree to the Terms & Conditions"
  ),
});

const SignUpPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [phoneExists, setPhoneExists] = useState(false);
  const fileInputRef = useRef(null);
  const { refreshAuthStatus } = usePermissions();
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [locationError, setLocationError] = useState(null);

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      company_name: "",
      client_name: "",
      email: "",
      phone_no: "",
      password: "",
      logo: "",
      agreeToTerms: false,
    },
  });

  const watchedEmail = watch("email");
  const watchedPhone = watch("phone_no");
  const planLocation = useLocation();
  const planData = planLocation.state?.plan;

  useEffect(() => {
    const getLocation = () => {
      if (!navigator.geolocation) {
        setLocationError("Geolocation is not supported by your browser.");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setLocationError(null);
        },
        (error) => {
          console.error("Error getting location:", error);
          setLocationError(
            "Unable to retrieve your location. Please allow location access."
          );
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    };

    getLocation();
  }, []);

  useEffect(() => {
    if (watchedEmail) {
      setEmailExists(false);
      clearErrors("email");
    }
  }, [watchedEmail, clearErrors]);

  useEffect(() => {
    if (watchedPhone) {
      setPhoneExists(false);
      clearErrors("phone_no");
    }
  }, [watchedPhone, clearErrors]);

  useEffect(() => {
    if (watchedEmail && watchedEmail.includes("@") && watchedEmail.length > 5) {
      const timer = setTimeout(async () => {
        setCheckingEmail(true);
        try {
          const exists = await checkAccountExistence({ email: watchedEmail });
          if (exists) {
            setEmailExists(true);
            setError("email", {
              type: "manual",
              message: "Email already exists. Please use a different email.",
            });
          } else {
            setEmailExists(false);
            clearErrors("email");
          }
        } catch (error) {
          console.error("Email check failed:", error);
          setEmailExists(false);
        } finally {
          setCheckingEmail(false);
        }
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      setCheckingEmail(false);
    }
  }, [watchedEmail, setError, clearErrors]);

  useEffect(() => {
    if (watchedPhone && watchedPhone.length > 10) {
      const timer = setTimeout(async () => {
        setCheckingPhone(true);
        try {
          const exists = await checkAccountExistence({ phone: watchedPhone });
          if (exists) {
            setPhoneExists(true);
            setError("phone_no", {
              type: "manual",
              message:
                "Phone number already exists. Please use a different phone number.",
            });
          } else {
            setPhoneExists(false);
            clearErrors("phone_no");
          }
        } catch (error) {
          console.error("Phone check failed:", error);
          setPhoneExists(false);
        } finally {
          setCheckingPhone(false);
        }
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      setCheckingPhone(false);
    }
  }, [watchedPhone, setError, clearErrors]);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setImagePreview(null);
      setUploadedImageUrl("");
      setValue("logo", "");
      return;
    }

    try {
      const imageURL = await validateImageFile(file);
      setImagePreview(imageURL);
      setImageUploading(true);

      const uploadResponse = await authApi.logoImage(file);
      if (uploadResponse?.status === "success" && uploadResponse?.tiny_url) {
        setUploadedImageUrl(uploadResponse.tiny_url);
        setValue("logo", uploadResponse.tiny_url);
        toast.success("Logo uploaded successfully!")
      } else {
        throw new Error("Upload failed: No tiny_url returned");
      }
    } catch (error) {
      handleApiResponse(error, "Failed to upload logo.", "POST");
      setImagePreview(null);
      setUploadedImageUrl("");
      setValue("logo", "");
      e.target.value = "";
    } finally {
      setImageUploading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const generatePassword = () => {
    const password = generateSecurePassword();
    setValue("password", password);
    setPasswordVisible(true);
  };

  const handlePlanSelect = async (plan) => {
    try {
      const response = await api.post("/stripe/create-checkout-session", {
        plan_id: plan.plan_id,
      });
      if (response.data.success && response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error("Invalid response from checkout API");
      }
    } catch (error) {
      console.error("Stripe Checkout API Error:", error);
      toast.error(
        error.response?.data?.message || "Failed to initiate checkout"
      );
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: "select_account",
      });
      const result = await signInWithPopup(auth, provider);
      const data = result.user;
      const user = {
        name: data.displayName,
        email: data.email,
        photo: data.photoURL,
      };
      const response = await authApi.google_login(
        user.name,
        user.email,
        user.photo,
        location.latitude,
        location.longitude
      );

      if (
        response.status === "error" &&
        response.message.includes("user not found")
      ) {
        throw new Error("User not found. Please sign up first.");
      }

      handleApiResponse(response, "Login Successfully", "POST");

      if (response.data.rememberMe) {
        localStorage.setItem(
          "rememberedCredentials",
          JSON.stringify({ email: user.email, rememberMe: true })
        );
      } else {
        localStorage.removeItem("rememberedCredentials");
      }

      const expiryTime = Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem("access_token", response.access_token);
      localStorage.setItem("token_expiry", expiryTime.toString());
      localStorage.setItem("user", JSON.stringify(response.data));
      localStorage.setItem("isExpired", response.data.isExpired);

      await saveFcmToken(response.data);
      if (planData) {
        await handlePlanSelect(planData);
      }
      await refreshAuthStatus();
      if (response.data.is_client_admin) {
        navigate("/dashboard", { replace: true });
      } else {
        navigate("/home", { replace: true });
      }
    } catch (error) {
      localStorage.clear();
      handleApiResponse(
        error,
        error.message ||
          "Google Sign-In Failed. Please sign up if you don't have an account.",
        "POST"
      );
    } finally {
      setGoogleLoading(false);
    }
  };

 const onSubmit = async (data) => {
  let hasErrors = false;

  if (watchedEmail) {
    try {
      if (emailExists) {
        setError("email", {
          type: "manual",
          message: "Email already exists. Please use a different email.",
        });
        hasErrors = true;
      }
    } catch (error) {
      console.error("Final email check failed:", error);
    }
  }

  if (data.phone_no) {
    try {
      if (phoneExists) {
        setError("phone_no", {
          type: "manual",
          message:
            "Phone number already exists. Please use a different phone number.",
        });
        hasErrors = true;
      }
    } catch (error) {
      console.error("Final phone check failed:", error);
    }
  }

  if (hasErrors) {
    return;
  }

  let countryCodeToSend = null;
  let currencyCodeToSend = null;
  let currencySymbolToSend = null;

  // Phone number se country code nikaalo
  if (data.phone_no) {
    const parsed = parsePhoneNumberFromString(data.phone_no);
    if (parsed?.country) {
      const code = parsed.country.toLowerCase(); // "SA" → "sa"
      countryCodeToSend = code;
      localStorage.setItem("country_code", code);

      // Ab is country code se currency fetch karo
      try {
        console.log("Fetching currency for country:", code.toUpperCase());
        
        const currencyRes = await fetch(
          `https://pevbackend.codovio.com/public/api/get-currency-codes?want_pagination=0`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        
        const currencyData = await currencyRes.json();
        console.log("Currency API Response:", currencyData);
        
        if (currencyData.success && currencyData.data && currencyData.data.length > 0) {
          // Find currency by country_code (uppercase from API)
          const countryCodeUpper = code.toUpperCase(); // "sa" → "SA"
          const currency = currencyData.data.find(
            (c) => c.country_code && c.country_code === countryCodeUpper
          );
          
          if (currency) {
            // Use the EXACT property names from API response
            currencyCodeToSend = currency.currency_code;
            currencySymbolToSend = currency.currency_symbol;
            
            console.log("Currency found:", { 
              countryCode: currency.country_code,
              currencyCode: currencyCodeToSend, 
              currencySymbol: currencySymbolToSend
            });
            
            localStorage.setItem("currency_code", currencyCodeToSend);
            localStorage.setItem("currency_symbol", currencySymbolToSend);
          } else {
            console.warn("No currency found for country:", countryCodeUpper);
          }
        }
      } catch (error) {
        console.error("Currency fetch failed:", error);
      }
    }
  }

  // Create FormData object
  const formData = new FormData();
  
  // Append all fields to FormData
  formData.append('company_name', data.company_name);
  formData.append('client_name', data.client_name);
  formData.append('email', data.email);
  formData.append('password', data.password);
  
  if (data.phone_no) {
    formData.append('phone_no', data.phone_no);
  }
  
  if (data.logo && data.logo.length > 0) {
    formData.append('logo', data.logo[0]); // Assuming logo is a file input
  }
  
  formData.append('product_id', '1');
  formData.append('sign_with', 'self');
  formData.append('latitude', location.latitude.toString());
  formData.append('longitude', location.longitude.toString());
  
  if (countryCodeToSend) {
    formData.append('country_code', countryCodeToSend);
  }
  
  if (currencyCodeToSend) {
    formData.append('currency_code', currencyCodeToSend);
  }
  
  if (currencySymbolToSend) {
    formData.append('currency_symbol', currencySymbolToSend);
  }

  // Log what's being appended to FormData
  console.log("FormData contents:");
  for (let pair of formData.entries()) {
    console.log(pair[0] + ': ' + pair[1]);
  }

  console.log("Submitting FormData with:", {
    country_code: countryCodeToSend,
    currency_code: currencyCodeToSend,
    currency_symbol: currencySymbolToSend
  });

  try {
    setIsLoading(true);
    const response = await authApi.clientSignup(formData);
    
    console.log("API Response:", response);
    
    if (response.data) {
      console.log("Response currency values:", {
        country_code: response.data.country_code,
        currency_code: response.data.currency_code,
        currency_symbol: response.data.currency_symbol
      });
    }
    
    handleApiResponse(
      response,
      "Registration successful! You can now login.",
      "POST"
    );

    if (planData) {
      navigate("/sign-in", {
        state: { plan: planData },
      });
    } else {
      navigate("/sign-in", { replace: true });
    }
  } catch (error) {
    console.error("Signup error details:", error);
    handleApiResponse(error, "Registration error", "POST");
  } finally {
    setIsLoading(false);
  }
};

  // const canSubmit =
  //   !isLoading && !imageUploading && !checkingEmail && !checkingPhone;
  const canSubmit =
    !isLoading && !imageUploading ;

  return (
    <>
      <style>
        {`
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          .auth-container {
  min-height: 100vh;
  background: black;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  position: relative;
  overflow: hidden;
}

/* Black gradient overlay - Top se thoda zyada neeche tak */
.auth-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 60%;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.9) 0%,
    rgba(0, 0, 0, 0.7) 50%,
    rgba(0, 0, 0, 0) 100%
  );
  z-index: 1;
  pointer-events: none;
}

.background-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}

          .auth-section {
            display: flex;
            // background: white;
            align-items: stretch;
            border-radius: 1rem;
            overflow: hidden;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
            max-width: 1200px;
            width: 100%;
            min-height: 650px;
            position: relative;
            z-index: 2;
            zoom:70%
          }

          /* Left Side - 50% width */
          .auth-left {
            flex: 0 0 40%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
  background: rgba(0, 0, 0, 0.6);  /* Yeh zyada dark kar diya */
            background: transparent;
            backdrop-filter: blur(10px);
            padding: 2rem 1.5rem;
            color: white;
            position: relative;
            overflow: hidden;
             border-left: 5px solid;
    border-top: 5px solid;
    border-bottom: 5px solid;
    border-image: linear-gradient(45deg, orange, red, white) 1;
    
    /* Animation */
    animation: borderAnimation 3s ease-in-out infinite;
          }

          .auth-left::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
  background: radial-gradient(circle at center, rgba(0, 0, 0, 0.3) 0%, transparent 70%);  /* Yeh bhi dark kar diya */
            pointer-events: none;
          }

          .auth-left-content {
            text-align: center;
            max-width: 400px;
            width: 100%;
            position: relative;
            z-index: 1;
          }

          .main-heading {
            font-size: 2rem;
            font-weight: 700;
            margin-bottom: 0.25rem;
            line-height: 1.2;
            text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          }

          .sub-heading {
            font-size: 1.25rem;
            font-weight: 400;
            margin-bottom: 2rem;
            opacity: 1;
              color: #a59e9dfe;


          }

          .pricing-list {
            text-align: left;
            margin-bottom: 1.5rem;
            position: relative;
          }

          .pricing-list::before {
  content: '';
  position: absolute;
  left: 15px;
  top: 36px;
  bottom: 53px;
  width: 2px;
  border-left: 2px dotted rgba(255, 255, 255, 0.5);
  z-index: 0;
}

         .pricing-item {
  margin-bottom: 1rem;
  position: relative;
  padding-left: 50px;
}

/* Remove the general ::before rule and add specific ones */

/* First item - Filled background with tick */
.pricing-item:nth-child(1)::before {
  content: '✓';
  position: absolute;
  left: 0;
  top: 0;
  width: 32px;
  height: 32px;
  background: white;
  color: #FF5733;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1;
}

/* Second and third items - Only border with tick */
.pricing-item:nth-child(2)::before,
.pricing-item:nth-child(3)::before {
  content: '✓';
  position: absolute;
  left: 0;
  top: 0;
  width: 32px;
  height: 32px;
  background: transparent;
  color: white;
  border: 2px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  z-index: 1;
}


          .pricing-item > div:first-child {
            background: white;
            border-radius: 10px;
            padding: 0.75rem 1rem;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            margin-bottom: 0.5rem;
          }

          .pricing-title {
            font-size: 0.9rem;
            font-weight: 600;
            margin-bottom: 0.25rem;
            color: #1a202c;
          }

          .pricing-description {
            font-size: 0.8rem;
            color: #718096;
            margin-bottom: 0;
          }

          .pricing-badge {
            background: rgba(255, 255, 255, 0.2);
            padding: 0.2rem 0.6rem;
            border-radius: 20px;
            font-size: 0.75rem;
            font-weight: 600;
            display: inline-block;
            margin-top: 0.25rem;
          }

          .pricing-price {
            font-size: 0.9rem;
            font-weight: 600;
            color: #1a202c;
          }

          .pricing-old-price {
            // text-decoration: line-through;
            opacity: 0.7;
            margin-right: 0.5rem;
          }

          .pricing-discount {
            color: #48bb78;
            font-weight: 600;
            font-size: 0.8rem;
            margin-top: 0.25rem;
            display: block;
          }
            .pricing-discount-second {
  color: #020202ff; /* Ya apna preferred color */
  font-weight: 600;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: block;
  text-decoration: none;
  // cursor: pointer;
}

.pricing-discount-second:hover {
  // color: #e63c23;
  // text-decoration: underline;
}


.click-link {
  color: #e63c23;
  text-decoration: none;
}

.click-link:hover {
  text-decoration: underline;
}

          .divider {
            height: 1px;
            background: rgba(255, 255, 255, 0.3);
            margin: 1.5rem 0;
          }

          .domain-discount {
            // background: rgba(255, 255, 255, 0.15);
            // border: 2px solid rgba(255, 255, 255, 0.3);
            // border-radius: 10px;
            // padding: 1rem;
            // text-align: center;
            // font-weight: 600;
            // font-size: 1rem;
            // backdrop-filter: blur(10px);
            // box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }

          /* Right Side - 50% width with scrollable form */
          // .auth-right {
          //   flex: 0 0 50%;
          //   display: flex;
          //   align-items: center;
          //   justify-content: center;
          //   padding: 2rem;
          //   background: white;
          //   overflow-y: auto;
          // }

          .auth-right {
  flex: 0 0 60%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  background: white;
  overflow-y: hidden;  /* auto se hidden kar diya */
  min-height: 100%;
  zoom: 85%;
}

          .auth-right::-webkit-scrollbar {
            width: 8px;
          }

          .auth-right::-webkit-scrollbar-track {
            background: #f1f1f1;
          }

          .auth-right::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }

          .auth-right::-webkit-scrollbar-thumb:hover {
            background: #555;
          }

          .auth-right {
  flex: 0 0 60%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  background: white;
  overflow-y: hidden;  /* auto se hidden kar diya */
  min-height: 100%;
  zoom: 80%;
}

.auth-form {
  width: 100%;
  max-height: 100%;  /* 100vh se 100% kar diya */
  background: white;
  overflow: hidden;  /* Yeh bhi add kar diya */
}

.auth-form h3 {
  font-size: 1.75rem;  /* 1.5rem se 1.75rem */
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 0.5rem;
  text-align: center;
}

.auth-form > p {
  color: #718096;
  margin: 0 0 2rem;  /* 1.5rem se 2rem */
  text-align: center;
  font-size: 1.3rem;  /* 0.875rem se 1rem */
}

          .auth-form .form-group {
  position: relative;
  margin-bottom: 1.5rem;  /* 1rem se 1.5rem */
}

          .auth-form label {
  display: block;
  font-size: 0.95rem;  /* 0.8rem se 0.95rem */
  color: #4a5568;
  margin-bottom: 0.5rem;  /* 0.35rem se 0.5rem */
  text-align: left;
  font-weight: 500;
}

          .auth-form input, .auth-form .PhoneInputInput {
  width: 100%;
  padding: 1rem 1rem 1rem 3.5rem;  /* 0.8rem se 1rem */
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  transition: all 0.3s;
  font-size: 1rem;  /* 0.95rem se 1rem */
  box-sizing: border-box;
  background: #f7fafc;
}

          .auth-form input:focus, .auth-form .PhoneInputInput:focus {
            outline: none;
            border-color: #E63C23;
            background: white;
            box-shadow: 0 0 0 3px rgba(230, 60, 35, 0.1);
          }

          .auth-form input.is-invalid, .auth-form .PhoneInputInput.is-invalid {
            border-color: #e53e3e;
          }

          .auth-form .input-icon-wrapper {
            position: relative;
          }

          .auth-form .icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 1.3rem;  /* 1.2rem se 1.3rem */
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 2;
}

          .auth-form .password-toggle {
            position: absolute;
            right: 0.9rem;
            top: 50%;
            transform: translateY(-50%);
            color: #a0aec0;
            cursor: pointer;
            font-size: 1.2rem;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 5;
          }

          .auth-form .password-toggle:hover {
            color: #E63C23;
          }

          .auth-form .password-hint {
  font-size: 0.8rem;  /* 0.7rem se 0.8rem */
  color: #e53e3e;
  margin-top: 0.5rem;
  text-align: left;
}

          .auth-form .checkbox-group {
            display: flex;
            align-items: flex-start;
            margin-bottom: 1rem;
          }

          .auth-form .checkbox-group .agree-terms {
            display: flex;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .auth-form .checkbox-group input[type="checkbox"] {
            width: 18px;
            height: 18px;
            margin: 0;
            padding: 0;
            flex-shrink: 0;
            cursor: pointer;
            border: 2px solid #e2e8f0;
            border-radius: 4px;
            appearance: none;
            background: #fff;
            position: relative;
            margin-top: 0.1rem;
          }

          .auth-form .checkbox-group input[type="checkbox"]:checked {
            background: #E63C23;
            border-color: #E63C23;
          }

          .auth-form .checkbox-group input[type="checkbox"]:checked::after {
            content: '';
            position: absolute;
            left: 5px;
            top: 2px;
            width: 5px;
            height: 10px;
            border: solid white;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
          }

         .auth-form .checkbox-group label {
  font-size: 0.9rem;  /* 0.8rem se 0.9rem */
  color: #718096;
  line-height: 1.5;
  cursor: pointer;
}

          .auth-form .submit-button {
            margin-top: 1rem;
          }

          .auth-form button[type="submit"] {
  width: 100%;
  padding: 1rem;  /* 0.75rem se 1rem */
  background: #E63C23;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1.05rem;  /* 0.95rem se 1.05rem */
  font-weight: 600;
  transition: background 0.3s;
  cursor: pointer;
}


          .auth-form button[type="submit"]:hover {
            background: #d1341b;
          }

          .auth-form button[type="submit"]:disabled {
            background: #cbd5e0;
            cursor: not-allowed;
          }

          .auth-form button.social-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background: white;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  margin-bottom: 1rem;
  padding: 1rem;  /* 0.75rem se 1rem */
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.3s;
  font-size: 1rem;  /* 0.9rem se 1rem */
}

          .auth-form button.social-btn:hover {
            background: #f7fafc;
            border-color: #cbd5e0;
          }

          .auth-form .register-link {
  text-align: center;
  margin-top: 1rem;
  font-size: 0.9rem;  /* 0.8rem se 0.9rem */
  color: #718096;
}

          .auth-form .register-link a {
            color: #E63C23;
            text-decoration: none;
            font-weight: 600;
            cursor: pointer;
             margin-bottom: 1rem;
          }

          .auth-form .register-link a:hover {
            text-decoration: underline;
          }

          .auth-form .file-upload-wrapper {
            position: relative;
            cursor: pointer;
          }

          .auth-form .file-upload-wrapper input[type="file"] {
            display: none;
          }

          .auth-form .action-icon {
            position: absolute;
            right: 0.7rem;
            top: 50%;
            transform: translateY(-50%);
            color: #a0aec0;
            font-size: 1.1rem;
            z-index: 5;
          }

          .auth-form .action-icon:hover {
            color: #E63C23;
          }

          .auth-form .uploading-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(255, 255, 255, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 0.5rem;
            z-index: 10;
          }

          .PhoneInputInput {
            padding-left: 3rem !important;
          }

          /* Two column layout for form rows */
          .form-row {
            display: flex;
            gap: 1rem;
            margin-bottom: 0;
          }
          
          .form-row .form-group {
            flex: 1;
            margin-bottom: 1rem;
          }

          .checking-indicator {
            position: absolute;
            right: 2.5rem;
            top: 50%;
            transform: translateY(-50%);
            color: #a0aec0;
            font-size: 0.7rem;
          }

          /* ========== RESPONSIVE STYLES ========== */

          /* Large screens (lg) - 1024px and above */
          @media (min-width: 1024px) {
            .auth-section {
              height: 95vh;
              max-height: 900px;
              zoom:70%
            }
          }

          /* Medium screens (md) - 768px to 1023px */
          @media (max-width: 1023px) and (min-width: 768px) {
            .auth-section {
              max-width: 95%;
              height: auto;
              min-height: 500px;
              zoom:70%
            }
            
            .auth-left{
              flex: 0 0 40%;
              padding: 1.5rem;
            }

             .auth-right {
              flex: 0 0 60%;
              padding: 1.5rem;
              zoom:75%
            }
            
            .auth-form {
              padding: 1.5rem;
              max-width: 100%;
            }
            
            .main-heading {
              font-size: 1.75rem;
            }
            
            .sub-heading {
              font-size: 1.1rem;
            }
            
            .pricing-item {
              padding-left: 45px;
            }
            
            .pricing-item::before {
              width: 28px;
              height: 28px;
              font-size: 14px;
            }
          }


         .auth-logo {
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  z-index: 10;
  background-color: white;
  padding: 0.5rem;
  border-radius: 0.5rem;
}

.auth-logo img {
  height: 30px;
  width: auto;
  object-fit: contain;
}

          /* Small screens (sm) - 767px and below */
          @media (max-width: 767px) {
            .auth-container {
              padding: 0.5rem;
              min-height: 100vh;
              align-items: flex-start;
            }
            
            .auth-section {
              flex-direction: column;
              height: auto;
              max-height: none;
              margin: 0.5rem 0;
              border-radius: 0.75rem;
              zoom:70%
            }
            
            .auth-left, .auth-right {
              flex: none;
              width: 100%;
            }
            
            .auth-left {
              padding: 2rem 1rem;
              order: 1;
            }
            
            .auth-right {
              padding: 1.5rem 1rem;
              order: 2;
              zoom:100%
            }
            
            .auth-form {
              padding: 0;
              max-width: 100%;
            }
            
            .main-heading {
              font-size: 1.5rem;
            }
            
            .sub-heading {
              font-size: 1rem;
              margin-bottom: 1.5rem;
            }
            
            .pricing-list {
  text-align: left;
  margin-bottom: 1.5rem;
  position: relative;
}
            
            .pricing-item {
              padding-left: 40px;
              margin-bottom: 0.75rem;
            }
            
            .pricing-item::before {
              width: 24px;
              height: 24px;
              font-size: 12px;
            }
            
            .pricing-item > div:first-child {
              padding: 0.5rem 0.75rem;
            }
            
            .pricing-title {
              font-size: 0.8rem;
            }
            
            .pricing-description {
              font-size: 0.7rem;
            }
            
            .domain-discount {
              // padding: 0.75rem;
              // font-size: 0.9rem;
            }
            
            .form-row {
              flex-direction: column;
              gap: 0;
            }
            
            .auth-form input, .auth-form .PhoneInputInput {
              padding: 0.75rem 1rem 0.75rem 2.5rem;
              font-size: 0.9rem;
            }
            
            .auth-form .icon {
              left: 0.8rem;
              font-size: 1.1rem;
            }
            
            .auth-form .password-toggle {
              right: 0.8rem;
              font-size: 1.1rem;
            }
            
            .auth-form h3 {
              font-size: 1.25rem;
            }
            
            .auth-form > p {
              font-size: 0.8rem;
              margin-bottom: 1rem;
            }
            
            .checkbox-group label {
              font-size: 0.75rem;
            }
          }

          /* Extra small screens (xs) - 480px and below */
          @media (max-width: 480px) {
            .auth-container {
              padding: 0.25rem;
            }
            
            .auth-section {
              margin: 0.25rem 0;
              border-radius: 0.5rem;
              zoom:70%
            }
            
            .auth-left {
              padding: 1.5rem 0.75rem;
            }
            
            .auth-right {
              padding: 1rem 0.75rem;
            }
            
            .main-heading {
              font-size: 1.25rem;
            }
            
            .sub-heading {
              font-size: 0.9rem;
            }
            
            .pricing-list::before {
              left: 12px;
            }
            
            .pricing-item {
              padding-left: 35px;
            }
            
            .pricing-item::before {
              width: 20px;
              height: 20px;
            }
            
            .auth-form button.social-btn {
              font-size: 0.8rem;
              padding: 0.6rem;
            }
            
            .auth-form button[type="submit"] {
              padding: 0.6rem;
              font-size: 0.9rem;
            }
          }

          /* Landscape orientation for mobile */
          @media (max-height: 600px) and (max-width: 767px) {
            .auth-container {
              align-items: flex-start;
              padding-top: 1rem;
            }
            
            .auth-section {
              max-height: 95vh;
              overflow-y: auto;
              zoom:70%
            }
          }
            @keyframes borderAnimation {
    0% {
        border-image: linear-gradient(45deg, orange, red, white) 1;
    }
    25% {
        border-image: linear-gradient(135deg, white, orange, red) 1;
    }
    50% {
        border-image: linear-gradient(225deg, red, white, orange) 1;
    }
    75% {
        border-image: linear-gradient(315deg, orange, red, white) 1;
    }
    100% {
        border-image: linear-gradient(45deg, orange, red, white) 1;
    }
}
        `}
      </style>

      <div className="auth-container">
        <div className="auth-logo">
          <img src={logo} alt="Logo" />
        </div>
        <video
          className="background-video"
          src="assets/video/auth-video-v2.mp4"
          autoPlay
          // loop
          muted
            // onEnded={(e) => e.target.pause()}

        />
        <section className="auth-section">
          {/* Left Side - Pricing Information */}
          <div className="auth-left">
            <div className="auth-left-content">
              <p className="main-heading h3">Start for free.</p>
              <p className="sub-heading h8">
                Ease your journey-we'll handle the rest.
              </p>

              <div className="pricing-list">
                <div className="pricing-item">
                  <div>
                    <div className="pricing-title">
                      Today – Free, 30-day trial
                    </div>
                    <div className="pricing-description">
                      Explore all features immediately
                    </div>
                  </div>
                  <div className="pricing-badge">Free</div>
                </div>

                <div className="pricing-item">
                  <div>
                    <div className="pricing-title">
                      Next – <span className="pricing-old-price">$19</span> use
                      the Starter Scale plan
                    </div>
                    <span className="pricing-discount-second">
                      for more subscription plans...
                      <a href="/" className="click-link">
                        click here
                      </a>
                    </span>
                  </div>
                </div>

                <div className="pricing-item">
                  <div>
                    <div className="pricing-title">
                      Always – No commitment, cancel anytime
                    </div>
                  </div>
                </div>
              </div>

              <div className="divider"></div>

              <div className="domain-discount"></div>
            </div>
          </div>

          {/* Right Side - Signup Form */}
          <div className="auth-right">
            <div className="auth-form">
              <p className="fw-bolder display-5 h4">Sign Up</p>
              <p>Fill in the details to park your first vehicle</p>

              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Company Name */}

                {/* Client Name & Email - Row on desktop, column on mobile */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="company_name">Company Name</label>
                    <div className="input-icon-wrapper">
                      <span className="icon">
                        <Icon icon="mdi:office-building" />
                      </span>
                      <Controller
                        name="company_name"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            id="company_name"
                            {...field}
                            placeholder="Company Name"
                            className={errors.company_name ? "is-invalid" : ""}
                            aria-describedby="company_name-error"
                            maxLength={50}
                          />
                        )}
                      />
                    </div>
                    {errors.company_name && (
                      <p id="company_name-error" className="password-hint">
                        {errors.company_name.message}
                      </p>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="client_name">Client Name</label>
                    <div className="input-icon-wrapper">
                      <span className="icon">
                        <Icon icon="f7:person" />
                      </span>
                      <Controller
                        name="client_name"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            id="client_name"
                            {...field}
                            placeholder="Client Name"
                            className={errors.client_name ? "is-invalid" : ""}
                            aria-describedby="client_name-error"
                            maxLength={20}
                          />
                        )}
                      />
                    </div>
                    {errors.client_name && (
                      <p id="client_name-error" className="password-hint">
                        {errors.client_name.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <div className="input-icon-wrapper">
                      <span className="icon">
                        <Icon icon="mage:email" />
                      </span>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="email"
                            id="email"
                            {...field}
                            placeholder="Email"
                            className={errors.email ? "is-invalid" : ""}
                            aria-describedby="email-error"
                          />
                        )}
                      />
                      {checkingEmail && (
                        <span className="checking-indicator"></span>
                      )}
                    </div>
                    {errors.email && (
                      <p id="email-error" className="password-hint">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                  {/* Phone & Password - Row on desktop, column on mobile */}
                  <div className="form-group">
                    <label htmlFor="phone_no">Phone Number</label>
                    <div className="input-icon-wrapper">
                      <CustomPhoneInput
                        name="phone_no"
                        control={control}
                        errors={errors}
                        label="Phone Number"
                        placeholder="Enter phone number"
                        labelName={false}
                      />
                      {checkingPhone && (
                        <span className="checking-indicator"></span>
                      )}
                    </div>
                    {errors.phone_no && (
                      <p id="phone_no-error" className="password-hint">
                        {errors.phone_no.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <div className="input-icon-wrapper">
                      <span className="icon">
                        <Icon icon="solar:lock-password-outline" />
                      </span>
                      <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                          <input
                            type={passwordVisible ? "text" : "password"}
                            id="password"
                            {...field}
                            placeholder="Password"
                            className={errors.password ? "is-invalid" : ""}
                            aria-describedby="password-error"
                          />
                        )}
                      />
                      <span className="password-toggle">
                        <span className="d-flex gap-1">
                          <span
                            onClick={generatePassword}
                            title="Generate secure password"
                          >
                            <Icon icon="carbon:password" />
                          </span>
                          <span
                            onClick={() => setPasswordVisible(!passwordVisible)}
                            title={
                              passwordVisible
                                ? "Hide password"
                                : "Show password"
                            }
                          >
                            <Icon
                              icon={
                                passwordVisible
                                  ? "ri:eye-off-line"
                                  : "ri:eye-line"
                              }
                            />
                          </span>
                        </span>
                      </span>
                    </div>
                    <p className="password-hint" style={{ color: "#718096" }}>
                      Password must be at least 8 characters and include
                      uppercase, lowercase, number, and special character.
                    </p>
                    {errors.password && (
                      <p id="password-error" className="password-hint">
                        {errors.password.message}
                      </p>
                    )}
                  </div>

                  {/* Logo */}
                  <div className="form-group">
                    <label htmlFor="logo">Logo</label>
                    <div
                      className="input-icon-wrapper file-upload-wrapper"
                      onClick={triggerFileInput}
                    >
                      <Controller
                        name="logo"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="text"
                            readOnly
                            placeholder="Select a logo image"
                            value={imagePreview ? "Logo selected" : ""}
                            aria-describedby="logo-error"
                          />
                        )}
                      />
                      <span
                        className="action-icon"
                        onClick={triggerFileInput}
                        title="Upload logo"
                      >
                        <Icon icon="ic:baseline-file-upload" />
                      </span>
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="file-input"
                        onChange={handleFileSelect}
                        accept=".png, .jpg, .jpeg"
                        disabled={imageUploading}
                      />
                      {imageUploading && (
                        <div className="uploading-overlay">
                          <div
                            className="spinner-border spinner-border-sm text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span className="ms-2 text-primary">
                            Uploading...
                          </span>
                        </div>
                      )}
                    </div>
                    {uploadedImageUrl && !imageUploading && (
                      <div className="mt-2 text-success">
                        Logo uploaded successfully!
                      </div>
                    )}
                    {errors.logo && (
                      <p id="logo-error" className="password-hint">
                        {errors.logo.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Terms Checkbox */}
                <div className="checkbox-group">
                  <div className="agree-terms">
                    <Controller
                      name="agreeToTerms"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          id="agreeToTerms"
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                    <label htmlFor="agreeToTerms">
                      I agree to the{" "}
                      <Link to="#" className="text-primary-600 fw-semibold">
                        Terms & Conditions
                      </Link>{" "}
                      and{" "}
                      <Link to="#" className="text-primary-600 fw-semibold">
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                </div>
                {errors.agreeToTerms && (
                  <p id="agreeToTerms-error" className="password-hint">
                    {errors.agreeToTerms.message}
                  </p>
                )}

                {/* Google Sign In */}
                <div className="social-signup mb-3">
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={googleLoading}
                    className="social-btn d-flex align-items-center justify-content-center gap-2"
                  >
                    {googleLoading ? (
                      <>
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Signing in...
                      </>
                    ) : (
                      <>
                        <Icon icon="logos:google-icon" width="20" height="20" />
                        Continue with Google
                      </>
                    )}
                  </button>
                </div>

                {/* Submit Button */}
                <div className="submit-button">
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="text-center"
                  >
                    {isLoading
                      ? "Please wait..."
                      : imageUploading
                      ? "Uploading logo..."
                      : checkingEmail || checkingPhone
                      ? "Sign Up"
                      : "Sign Up"}
                  </button>
                </div>

                <p className="register-link">
                  Already have an account?{" "}
                  <span
                    onClick={() => {
                      if (planData) {
                        navigate("/sign-in", {
                          state: { plan: planData },
                        });
                      } else {
                        navigate("/sign-in", { replace: true });
                      }
                    }}
                  >
                    <Link>Sign In</Link>
                  </span>
                </p>
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SignUpPage;
